import { getCartBundle } from "@/cart/cartBundle";
import type { Cart } from "@/graphql/storefront/generated";
import { isMatic, isReferralRewardBagPass } from "@/utils/ShopifyHelpers";

export type CartLineNode = Cart["lines"]["edges"][number]["node"];

export type DisplayCartItem = {
  id: string;
  item: CartLineNode;
  lines: CartLineNode[];
  quantity: number;
};

const ANNUAL_BAG_PASS_DISPLAY_TITLE = "Annual Bag Pass";

const getOriginalUnitAmount = (line: CartLineNode): number =>
  Number(line.merchandise.price.amount);

export const getLineEffectiveTotalAmount = (line: CartLineNode): number =>
  Number(
    line.cost?.totalAmount?.amount ??
      getOriginalUnitAmount(line) * line.quantity,
  );

export const getLineEffectiveUnitAmount = (line: CartLineNode): number =>
  line.quantity > 0
    ? getLineEffectiveTotalAmount(line) / line.quantity
    : getOriginalUnitAmount(line);

export const getCartLines = (cart?: Cart): CartLineNode[] =>
  cart?.lines?.edges?.map(({ node }) => node) ?? [];

export const getVariantQuantity = (
  cart: Cart | undefined,
  variantId: string,
): number =>
  getCartLines(cart).reduce(
    (total, line) =>
      line.merchandise.id === variantId ? total + line.quantity : total,
    0,
  );

export const getMaticCount = (cart?: Cart): number =>
  getCartLines(cart).reduce(
    (total, line) => (isMatic(line) ? total + line.quantity : total),
    0,
  );

export const getCartLineDisplayTitle = (line: CartLineNode): string => {
  if (isReferralRewardBagPass(line)) {
    return ANNUAL_BAG_PASS_DISPLAY_TITLE;
  }

  return line.merchandise.product.title;
};

/**
 * Lines of one variant draw as one item. The lines of a landing-page package
 * draw as one item too, led by its robot: its quantity is the robot's, since
 * every line in the package carries the same count.
 */
export const getDisplayCartItems = (cart?: Cart): DisplayCartItem[] => {
  const orderedLines = [...getCartLines(cart)].reverse();
  const groupedItems = new Map<string, DisplayCartItem>();

  for (const line of orderedLines) {
    const bundle = getCartBundle(line);
    const key = bundle === null ? line.merchandise.id : bundle.key;
    const existingItem = groupedItems.get(key);

    if (existingItem) {
      existingItem.lines.push(line);
      if (bundle === null) {
        existingItem.quantity += line.quantity;
      } else if (isMatic(line)) {
        existingItem.item = line;
        existingItem.quantity = line.quantity;
      }
      continue;
    }

    groupedItems.set(key, {
      id: key,
      item: line,
      lines: [line],
      quantity: line.quantity,
    });
  }

  return Array.from(groupedItems.values());
};

/**
 * The count a shopper sees: a landing-page package is one item, not its
 * lines. Analytics keep the cart's own totalQuantity.
 */
export const getDisplayCartQuantity = (cart?: Cart): number =>
  getDisplayCartItems(cart).reduce((total, item) => total + item.quantity, 0);
